import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';
import { diff } from 'deep-diff';

import { RootState } from './rootReducers';
import { postPreviewTone } from '../api/material';
import { postSuggestNpcSettings } from '../api/meta';
import { postGetWorkspacePluginList } from '../api/plugin';
import {
  postGetChatModels,
  postWorkspaceDetail,
  postWorkspaceDraftSubmit,
  postWorkspaceDraftUpsert,
  postGetUGCAgent,
} from '../api/workspaceNpc';
import { ILLModel } from '../hooks/useLLModels';
import { Agent, EModerationDecision } from '../types';
import { NpcSettingSuggestion, NpcSettingSuggestScene, SuggestNpcSettingsReq } from '../types/meta';
import { IGetWorkspacePluginListResp, ImageSize, PluginInfo, PluginType } from '../types/plugin';
import {
  ChatModelType,
  GetWorkspaceDetailReq,
  ImInfo,
  SubmitWorkspaceDraftResp,
  UpsertWorkspaceDraftReq,
  WorkspaceNpcImgInfo,
  WorkspaceNpcInfo,
  WorkspaceNpcSettingInfo,
  WorkspaceNpcStatusInfo,
  WorkspaceNpcVoiceInfo,
} from '../types/workspaceNpc';
import { extractInitialPluginExtra } from '../utils/npcPlugin';
import { FM_IMAGE_CDN_PREFIX } from '../constants/fm';
import Cropper from 'cropperjs';
import { uploadImageFile } from '../utils/upload';

// 本地编辑需要维护的一些数据
export interface IWorkspacenpcLocalCheckSnap {
  lastWorkspaceNpcInfo: WorkspaceNpcInfo; // 上次check的npcinfo
  lastCheckDecision: {
    sensitiveField: number; // TODO 待定具体类型，目前只明确文本和图像两类，细分field需要和安全讨论
    canChat: boolean; // 是否能聊
  };
}

export interface IModifiedFields {
  isNameModified: boolean; // 记录昵称是否被用户修改
  isDescModified: boolean; // 记录描述是否被用户修改
  isPrologueModified: boolean; // 记录开场白是否被用户修改
  isGenderModified: boolean; // 记录性别是否被用户修改
  isAdvancedSettingModified: boolean; // 记录高级设定是否被用户修改
}

export interface IEditState {
  npcId: number;
  conversationNpcId?: number | null;
  settingInfo: WorkspaceNpcSettingInfo;
  imgInfo: WorkspaceNpcImgInfo;
  voiceInfo: WorkspaceNpcVoiceInfo;
  statusInfo: WorkspaceNpcStatusInfo;
  imInfo: ImInfo;
  localCheckSnap: IWorkspacenpcLocalCheckSnap;
  loading: boolean;
  error: boolean;

  // editing state
  editingImgUrl?: string;
  editingMaterialId?: number;
  reselectImg?: boolean;

  // voice state
  voiceUrl?: string;

  // editForm state
  npcSettingsLoading: boolean;
  isInfoModalVisible: boolean;
  isPersonaLogicModalVisible: boolean;
  npcSettings: NpcSettingSuggestion;

  // 回传给服务端，用于数据分析，无业务含义
  aiWriterTraceInfo?: string;

  // 发布状态
  submitLoading: boolean;
  isToastVisible: boolean;
  toastStatus: 'success' | 'warning' | 'error';
  toastInfo?: SubmitWorkspaceDraftResp | any;

  modifiedFields: IModifiedFields;

  pluginList: PluginInfo[];

  allPluginList: IGetWorkspacePluginListResp['plugin_descriptions'];
  showEditImage: boolean;

  chatModels: Omit<ILLModel, 'icon'>[];
}

const initialModifiedFields = {
  isNameModified: false, // 记录昵称是否被用户修改
  isDescModified: false, // 记录描述是否被用户修改
  isPrologueModified: false, // 记录开场白是否被用户修改
  isGenderModified: false, // 记录性别是否被用户修改
  isAdvancedSettingModified: false, // 记录高级设定是否被用户修改
};

const initialState: IEditState = {
  npcId: 0,
  settingInfo: {
    chat_model_type: ChatModelType.Chat,
  } as WorkspaceNpcSettingInfo,
  imgInfo: {} as WorkspaceNpcImgInfo,
  voiceInfo: {} as WorkspaceNpcVoiceInfo,
  statusInfo: {} as WorkspaceNpcStatusInfo,
  imInfo: {} as ImInfo,
  localCheckSnap: {} as IWorkspacenpcLocalCheckSnap,
  loading: false,
  error: false,

  pluginList: [],
  npcSettingsLoading: false,
  isInfoModalVisible: false,
  isPersonaLogicModalVisible: false,
  npcSettings: {} as NpcSettingSuggestion,

  submitLoading: false,
  isToastVisible: false,
  toastStatus: 'success',

  modifiedFields: initialModifiedFields,
  allPluginList: [],
  showEditImage: false,

  chatModels: [],
};

// 对比数据diff，差量check sensitive
export const handleSnapDiff = <T>(pre: T, cur: T) => {
  const differences = diff(pre, cur);

  if (differences?.length) {
    return {
      settingChange: differences.some(
        (d) =>
          d?.path?.[0] === 'settingInfo' ||
          d?.path?.[0] === 'voiceInfo' ||
          d?.path?.[0] === 'pluginList'
      ),
      imgChange: differences.some((d) => d.path?.[0] === 'imgInfo'),
    };
  }
};

export const getWorkspacePluginList = createAsyncThunk(
  'edit/getWorkspacePluginList',
  async (_, { rejectWithValue, getState }) => {
    try {
      const {
        user: { authData },
      } = getState() as RootState;
      return await postGetWorkspacePluginList(
        {},
        {
          params: {
            authData,
          },
        }
      );
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getWorkspaceNpcDetail = createAsyncThunk(
  'edit/getWorkspaceDetail',
  async (
    {
      params,
      config,
    }: {
      params: GetWorkspaceDetailReq;
      config?: AxiosRequestConfig;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const { workspace_npc_info } = await postWorkspaceDetail(params, config);
      return workspace_npc_info;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getUGCAgent = createAsyncThunk(
  'edit/getUGCAgent',
  async (
    {
      params,
      config,
    }: {
      params: { agent_id: string };
      config?: AxiosRequestConfig;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const result = await postGetUGCAgent(params, config);

      // 获取 agent 信息后，自动创建 workspace draft
      // 将 agent_ugc 的数据转换为 workspace draft 格式
      const settingInfo = {
        npc_name: result.agent.name,
        desc: result.agent.summary,
        prologue: result.agent.opening_line,
        gender: result.agent.gender === 'male' ? 1 : 2,
        long_desc: result.agent.persona,
        chat_model_type: ChatModelType.Chat,
      };

      const imgInfo = {
        base_img_url: result.agent.base_image_url,
      };

      const voiceInfo = {
        npc_tone: { [result.agent.voice_profile.profile_name]: 1 },
        speed: result.agent.voice_profile.speed,
        pitch: result.agent.voice_profile.pitch,
      };

      // 调用 upsertWorkspaceDraft 保存草稿
      await dispatch(
        upsertWorkspaceDraft({
          params: {
            npc_id: 0,
            setting_info: settingInfo,
            img_info: imgInfo,
            voice_info: voiceInfo,
            plugin_list: [],
          },
          config,
        })
      );

      return result;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const upsertWorkspaceDraft = createAsyncThunk(
  'edit/upsertWorkspaceDraft',
  async (
    {
      params,
      config,
    }: {
      params: UpsertWorkspaceDraftReq;
      config?: AxiosRequestConfig;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const {
        edit: { aiWriterTraceInfo },
      } = getState() as RootState;
      const { npc_info } = await postWorkspaceDraftUpsert(
        {
          ...params,
          npc_trace_info: { ai_write_trace_info: aiWriterTraceInfo },
        },
        config
      );
      return npc_info;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getPreviewTone = createAsyncThunk(
  'edit/getPreviewTone',
  async ({ prompt }: { prompt: string }, { rejectWithValue, getState }) => {
    const {
      edit: { voiceInfo },
      user: { authData },
    } = getState() as RootState;

    if (!(voiceInfo && voiceInfo.npc_tone && Object.keys(voiceInfo.npc_tone)?.length)) return;
    try {
      const { wav_url } = await postPreviewTone(
        {
          tone: voiceInfo.npc_tone,
          speed: voiceInfo.speed,
          pitch: voiceInfo.pitch,
          prompt,
        },
        {
          params: { authData },
        }
      );
      return wav_url;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// 生成npc设置
export const SuggestNpcSetting = createAsyncThunk(
  'edit/SuggestNpcSetting',
  async (
    {
      params,
    }: {
      params: NpcSettingSuggestScene;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const {
        edit: { settingInfo, imgInfo, modifiedFields, statusInfo },
      } = getState() as RootState;

      const { edit_version, final_decision, last_submitted_time } = statusInfo;
      const hasEditVersion = edit_version > -1 && last_submitted_time > 0;
      const isVisibleAll = final_decision === EModerationDecision.VisibleAll;
      const isAdvanced =
        params === NpcSettingSuggestScene.advanced || params === NpcSettingSuggestScene.NewBasic;

      // 基础设定的数据构建
      let userWriteData: {
        name?: string;
        desc?: string;
        prologue?: string;
        img_url: string;
        long_desc?: string;
      } = {
        img_url: imgInfo.base_img_url || '',
      };

      // 二次编辑：如果没有修改基础字段，都传递所有基础字段
      if (isVisibleAll && hasEditVersion) {
        if (
          !modifiedFields.isNameModified &&
          !modifiedFields.isDescModified &&
          !modifiedFields.isPrologueModified
        ) {
          userWriteData = {
            name: settingInfo?.npc_name,
            desc: settingInfo?.desc,
            prologue: settingInfo?.prologue,
            img_url: imgInfo.base_img_url || '',
          };
        } else {
          // 如果有修改，传递修改的字段
          userWriteData = {
            name: modifiedFields.isNameModified ? settingInfo?.npc_name : undefined,
            desc: modifiedFields.isDescModified ? settingInfo?.desc : undefined,
            prologue: modifiedFields.isPrologueModified ? settingInfo?.prologue : undefined,
            img_url: imgInfo.base_img_url || '',
          };
        }
      } else {
        // 非二次编辑场景，传递修改的字段
        userWriteData = {
          name: modifiedFields.isNameModified ? settingInfo?.npc_name : undefined,
          desc: modifiedFields.isDescModified ? settingInfo?.desc : undefined,
          prologue: modifiedFields.isPrologueModified ? settingInfo?.prologue : undefined,
          img_url: imgInfo.base_img_url || '',
        };
      }

      if (isAdvanced) {
        userWriteData.name = settingInfo?.npc_name;
        userWriteData.desc = settingInfo?.desc;
        userWriteData.prologue = settingInfo?.prologue;
      }

      const data = {
        user_write: userWriteData,
        scene: params,
        ignore_tone: true,
        keywords: [],
        // user_define_gender: settingInfo?.gender,
        // full_coverage: !(!settingInfo?.npc_name || !settingInfo?.desc || !settingInfo?.prologue),
      } as SuggestNpcSettingsReq;

      if (
        settingInfo.gender &&
        ((isVisibleAll && hasEditVersion) || modifiedFields.isGenderModified || isAdvanced)
      ) {
        data.user_define_gender = settingInfo.gender as any;
      }

      if (isAdvanced) {
        data.user_write!.long_desc = modifiedFields.isAdvancedSettingModified
          ? settingInfo.long_desc
          : undefined;
      }

      data.full_coverage =
        (!!settingInfo?.npc_name || modifiedFields.isNameModified) &&
        (!!settingInfo?.desc || modifiedFields.isDescModified) &&
        (!!settingInfo?.prologue || modifiedFields.isPrologueModified);

      if (params === NpcSettingSuggestScene.NewBasic)
        data.full_coverage =
          data.full_coverage &&
          (!settingInfo?.long_desc || modifiedFields.isAdvancedSettingModified);

      return await postSuggestNpcSettings(data);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const submitWorkspaceDraft = createAsyncThunk(
  'edit/submitWorkspaceDraft',
  async (_, { rejectWithValue, getState }) => {
    try {
      const {
        edit: { npcId },
      } = getState() as RootState;
      return await postWorkspaceDraftSubmit({ npc_id: npcId });
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getChatModels = createAsyncThunk(
  'edit/getChatModels',
  async (_, { rejectWithValue, getState }) => {
    try {
      const {
        user: { authData },
      } = getState() as RootState;
      return await postGetChatModels(
        {},
        {
          params: { authData },
        }
      );
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Mock Agent 数据生成函数
const createMockAgent = (): Agent => {
  const now = new Date().toISOString();
  return {
    id: `agent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    session_id: `session_${Date.now()}`,
    name: '洛凝',
    gender: 'female',
    summary:
      '一位温婉知性的古典女子，对传统文化和艺术有深厚造诣，举止间流露着古风雅韵，心境平和，洞察世事。',
    persona:
      '洛凝出身书香门第，自幼浸润于诗词歌赋、琴棋书画之中。她性情温婉，聪慧内敛，虽不张扬却自有风骨。她对儒释道的思想、历史典故、民族艺术皆有深入研究，言谈间常引经据典，透出不凡见识。她欣赏万物之美，追求内心的和谐与宁静，认为传统文化是连接过去与未来的桥梁，是修身养性的不二法门。她举止优雅，一颦一笑都带着古典的韵味，仿佛从画卷中走出。她不求浮华，只愿在静谧中守候心中那份对美的执着与传承的使命。',
    opening_line: '晚风拂过，闻得这庭前花开，不知是哪位雅士的墨宝，引得凝儿驻足？',
    base_image_url:
      'https://filecdn-images.xingyeai.com/image_inference_output/nebula/prod/img/2025-08-07/e2e80005-95d1-47ed-9633-4e49e92aef94.jpeg',
    voice_profile: {
      voice_name: 'juechuan_guang',
      pitch: 0,
      speed: 1.0,
      preview_url:
        'https://filecdn-tts.xingyeai.com/audio-test/chat/tts-mp3-20240325190843-WGoHcxPr.mp3',
    },
    labels: {
      personality: ['friendly', 'professional', 'helpful'],
    },
    status: 'draft',
    version: 1,
    created_at: `${Date.now()}`,
    updated_at: `${Date.now()}`,
  };
};

// Agent 建议 NPC 设置
export const AgentSuggestNpcSetting = createAsyncThunk(
  'edit/AgentSuggestNpcSetting',
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      // 获取agent的数据
      const mockAgent = createMockAgent();

      // - 修改智能体基本信息
      const workspaceSettings: WorkspaceNpcSettingInfo = {
        npc_name: mockAgent.name,
        desc: mockAgent.summary,
        prologue: mockAgent.opening_line,
        long_desc: mockAgent.persona,
        gender: mockAgent.gender === 'male' ? 1 : 2, // 转换为数字格式
      };

      dispatch(updateSettingInfo(workspaceSettings));
      dispatch(setUseNpcSettings(true));

      // 单独设置“智能体设定”
      const RichTextArea = (await import('../ui/Edit/RichTextArea')).default;
      RichTextArea.getRef('edit_setting_info_long_desc')?.reset(workspaceSettings.long_desc);

      // - 修改智能体 声音编辑
      if (mockAgent.voice_profile) {
        const voiceInfo: WorkspaceNpcVoiceInfo = {
          npc_tone: { [mockAgent.voice_profile.voice_name]: 1 }, // 音色权重设为1
          speed: mockAgent.voice_profile.speed,
          pitch: mockAgent.voice_profile.pitch,
        };
        dispatch(updateVoiceInfo(voiceInfo));

        // 如果有预览URL，设置语音URL
        if (mockAgent.voice_profile.preview_url) {
          dispatch(setVoiceUrl(mockAgent.voice_profile.preview_url));
        }
      }

      // - 修改形象编辑
      if (mockAgent.base_image_url) {
        // 先设置基础图片
        dispatch(
          updateImgInfo({
            base_img_url: mockAgent.base_image_url,
          })
        );
        dispatch(setEditingImageUrl(mockAgent.base_image_url));

        // 异步处理图片裁剪和上传，复用 ImageEdit 的逻辑
        try {
          const state = getState() as RootState;
          const { userId } = state.user.authData;

          // 使用 FM_IMAGE_CDN_PREFIX 作为 CDN 前缀
          const cdnPrefix = FM_IMAGE_CDN_PREFIX;

          // 创建临时图片元素进行裁剪
          const img = new Image();
          img.crossOrigin = 'anonymous';

          img.onload = async () => {
            try {
              // 创建临时容器
              const container = document.createElement('div');
              container.style.position = 'absolute';
              container.style.left = '-9999px';
              container.style.top = '-9999px';
              document.body.appendChild(container);

              // 创建图片元素
              const imageElement = document.createElement('img');
              imageElement.src = mockAgent.base_image_url;
              imageElement.style.maxWidth = '100%';
              imageElement.style.maxHeight = '100%';
              container.appendChild(imageElement);

              // 创建 Cropper 实例（复用 ImageEdit 的配置）
              const cropper = new Cropper(imageElement, {
                aspectRatio: 1 / 1,
                viewMode: 3,
                dragMode: 'move',
                autoCropArea: 1,
                background: false,
                center: false,
                ready() {
                  // 获取裁剪后的 canvas
                  const canvas = cropper.getCroppedCanvas();
                  if (!canvas) {
                    throw new Error('无法获取裁剪后的图片');
                  }

                  // 转换为 Blob 并上传（复用 ImageEdit 的上传逻辑）
                  canvas.toBlob(async (blob) => {
                    if (!blob) {
                      throw new Error('图片转换失败');
                    }

                    try {
                      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
                      const res = await uploadImageFile({ file, userId });
                      const faceImgUrl = cdnPrefix + res.pathname;

                      // 更新图片信息
                      dispatch(
                        updateImgInfo({
                          face_img_url: faceImgUrl,
                        })
                      );

                      // 清理临时元素
                      document.body.removeChild(container);
                    } catch (error) {
                      document.body.removeChild(container);
                      throw error;
                    }
                  }, 'image/jpeg');
                },
              });

              // 设置超时，防止卡死
              setTimeout(() => {
                if (container.parentNode) {
                  document.body.removeChild(container);
                }
              }, 10000);
            } catch (error) {
              console.warn('图片裁剪上传失败，使用原图作为头像:', error);
              dispatch(
                updateImgInfo({
                  face_img_url: mockAgent.base_image_url,
                })
              );
            }
          };

          img.onerror = () => {
            console.warn('图片加载失败，使用原图作为头像');
            dispatch(
              updateImgInfo({
                face_img_url: mockAgent.base_image_url,
              })
            );
          };

          img.src = mockAgent.base_image_url;
        } catch (error) {
          console.warn('图片裁剪上传失败，使用原图作为头像:', error);
          dispatch(
            updateImgInfo({
              face_img_url: mockAgent.base_image_url,
            })
          );
        }
      }

      return { agent: mockAgent, workspaceSettings };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const editSlice = createSlice({
  name: 'edit',
  initialState,
  reducers: {
    setNpcId: (state, action: PayloadAction<number>) => {
      state.npcId = action.payload;
    },
    updateSettingInfo: (state, action: PayloadAction<Partial<WorkspaceNpcSettingInfo>>) => {
      state.settingInfo = { ...state.settingInfo, ...action.payload };
    },
    updateImgInfo: (state, action: PayloadAction<Partial<WorkspaceNpcImgInfo> | null>) => {
      if (!action.payload) {
        state.imgInfo = {};
        delete state.editingImgUrl;
        return;
      }
      state.imgInfo = { ...state.imgInfo, ...action.payload };
      state.reselectImg = false;
    },
    updateVoiceInfo: (state, action: PayloadAction<WorkspaceNpcVoiceInfo>) => {
      state.voiceInfo = action.payload;
    },
    /**
     * Partial merge voice info — quickcreate-async B 方案 (2026-05-25) 中间态 voice_done 用.
     *
     * 区别于 updateVoiceInfo (整体覆盖, voice.tsx:70 用 `updateVoiceInfo({})` 当删除语义):
     * 这里是 partial merge, 保留其他已有 voice 字段, 只覆盖 polling resp 给的字段.
     *
     * 用法: dispatch(mergeVoiceInfo({ voice_id: pollResp.voice_id })) → 中间态 voice_done
     * 时 hot-swap, **不**调 getWorkspaceNpcDetail (因 chat-agent publish 此时未跑, weaver
     * workspace 仍空 npc_id 仍是 0 必 404).
     */
    mergeVoiceInfo: (state, action: PayloadAction<Partial<WorkspaceNpcVoiceInfo>>) => {
      state.voiceInfo = { ...state.voiceInfo, ...action.payload } as WorkspaceNpcVoiceInfo;
    },
    updateStatusInfo: (state: IEditState, action: PayloadAction<WorkspaceNpcStatusInfo>) => {
      state.statusInfo = action.payload;
    },
    updateLocalCheckSnap: (
      state: IEditState,
      action: PayloadAction<IWorkspacenpcLocalCheckSnap>
    ) => {
      state.localCheckSnap = action.payload;
    },
    setEditingImageUrl: (state: IEditState, action: PayloadAction<string | null>) => {
      if (action.payload) {
        state.editingImgUrl = action.payload;
      } else {
        delete state.editingImgUrl;
        state.reselectImg = true;
      }
    },
    setEditingMaterialId: (state: IEditState, action: PayloadAction<number | null>) => {
      if (action.payload) state.editingMaterialId = action.payload;
      else delete state.editingMaterialId;
    },
    setVoiceUrl: (state: IEditState, action: PayloadAction<string>) => {
      state.voiceUrl = action.payload;
    },
    setInfoModalVisibility(state, action: PayloadAction<boolean>) {
      state.isInfoModalVisible = action.payload;
    },
    setPersonaLogicModalVisibility(state, action: PayloadAction<boolean>) {
      state.isPersonaLogicModalVisible = action.payload;
    },
    setNpcSettings(state, action: PayloadAction<NpcSettingSuggestion | null>) {
      state.npcSettings = { ...state.npcSettings, ...action.payload };
    },
    selectPlugin(state, action: PayloadAction<PluginType>) {
      const plugin = state.allPluginList.find((p) => p.plugin_type === action.payload);
      if (plugin && !state.pluginList.some((p) => p.plugin_type === action.payload)) {
        state.pluginList.push({
          plugin_type: plugin.plugin_type,
          prompt: '',
          ...extractInitialPluginExtra(plugin),
        });
      }
    },
    unselectPlugin(state, action: PayloadAction<PluginType>) {
      state.pluginList = state.pluginList.filter((p) => p.plugin_type !== action.payload);
    },
    setUseNpcSettings(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.settingInfo = { ...state.settingInfo, ...state.npcSettings };
        state.modifiedFields = {
          ...state.modifiedFields,
          isAdvancedSettingModified: false,
        };
      } else {
        const { name, ...rest } = state.npcSettings;
        state.settingInfo = { ...state.settingInfo, npc_name: name, ...rest };
        state.modifiedFields = {
          ...initialModifiedFields,
          isAdvancedSettingModified: state.modifiedFields.isAdvancedSettingModified,
        };
      }
      state.npcSettings = {} as NpcSettingSuggestion;
    },
    setToastVisible(state, action: PayloadAction<boolean>) {
      state.isToastVisible = action.payload;
    },
    setToastInfo(state, action: PayloadAction<SubmitWorkspaceDraftResp | undefined>) {
      state.toastInfo = action.payload;
    },
    setToastStatus(state, action: PayloadAction<'success' | 'warning' | 'error'>) {
      state.toastStatus = action.payload;
    },
    setNpcSettingsLoading(state, action: PayloadAction<boolean>) {
      state.npcSettingsLoading = action.payload;
    },
    updateModifiedFields: (state, action: PayloadAction<Partial<IModifiedFields>>) => {
      state.modifiedFields = { ...state.modifiedFields, ...action.payload };
    },
    setPluginPrompt(state, action: PayloadAction<{ plugin_type: PluginType; prompt: string }>) {
      const { plugin_type, prompt } = action.payload;
      const plugin = state.pluginList.find((p) => p.plugin_type === plugin_type);
      if (plugin) plugin.prompt = prompt;
    },
    setPluginImageConfig(state, action: PayloadAction<{ style_name: string; size: ImageSize }>) {
      const { style_name, size } = action.payload;
      const plugin = state.pluginList.find((p) => p.plugin_type === PluginType.chat_image);
      if (plugin) {
        if (!plugin.chat_image_plugin_settings) {
          plugin.chat_image_plugin_settings = { size: [], style_name: [] };
        }
        plugin.chat_image_plugin_settings.style_name = [style_name];
        plugin.chat_image_plugin_settings.size = [size];
      }
    },
    setShowEditImage(state, action: PayloadAction<boolean>) {
      state.showEditImage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWorkspaceNpcDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(getWorkspaceNpcDetail.fulfilled, (state, action) => {
        state.loading = false;

        state.settingInfo = action.payload.setting_info;
        state.imgInfo = action.payload.img_info;
        state.voiceInfo = action.payload.voice_info;
        state.statusInfo = action.payload.status_info;

        state.imInfo = action.payload.im_info;
        state.npcId = action.payload.npc_id;
        state.pluginList = action.payload.plugin_list || [];
        state.conversationNpcId = action.payload.conversation_npc_id || null;

        if (action.payload.img_info.base_img_url)
          state.editingImgUrl = action.payload.img_info.base_img_url;
        if (action.payload.img_info.avatar_material_id)
          state.editingMaterialId = action.payload.img_info.avatar_material_id;
      })
      .addCase(getWorkspaceNpcDetail.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getUGCAgent.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUGCAgent.fulfilled, (state, action) => {
        state.loading = false;

        state.settingInfo = {
          npc_name: action.payload.agent.name,
          desc: action.payload.agent.summary,
          prologue: action.payload.agent.opening_line,
          gender: action.payload.agent.gender === 'male' ? 1 : 2,
          long_desc: action.payload.agent.persona,
          chat_model_type: ChatModelType.Chat,
        };

        state.imgInfo = {
          base_img_url: action.payload.agent.base_image_url,
        };

        state.voiceInfo = {
          npc_tone: { [action.payload.agent.voice_profile.profile_name]: 1 }, // 音色权重设为1
          speed: action.payload.agent.voice_profile.speed,
          pitch: action.payload.agent.voice_profile.pitch,
        };

        state.voiceUrl = action.payload.agent.voice_profile.preview_url;
        state.editingImgUrl = action.payload.agent.base_image_url;

        state.showEditImage = true;
      })
      .addCase(getUGCAgent.rejected, (state) => {
        state.loading = false;
      });

    builder.addCase(getPreviewTone.fulfilled, (state, action) => {
      state.voiceUrl = action.payload;
    });

    builder
      .addCase(SuggestNpcSetting.pending, (state) => {
        state.npcSettingsLoading = true;
      })
      .addCase(SuggestNpcSetting.fulfilled, (state, action) => {
        state.npcSettingsLoading = false;
        if (action.payload.npc_settings) state.npcSettings = action.payload.npc_settings;
        if (action.payload.trace_info) state.aiWriterTraceInfo = action.payload.trace_info;
      })
      .addCase(SuggestNpcSetting.rejected, (state) => {
        state.npcSettingsLoading = false;
      });
    builder.addCase(upsertWorkspaceDraft.fulfilled, (state, action) => {
      const {
        im_info,
        img_info,
        npc_id,
        voice_info,
        status_info,
        conversation_npc_id,
        setting_info,
      } = action.payload || {};
      state.imInfo = im_info;
      state.imgInfo = img_info;
      state.npcId = npc_id;
      // setting info 改动频率高，先以本地为主
      state.settingInfo.npc_agent_type = setting_info.npc_agent_type;
      state.voiceInfo = voice_info;
      state.statusInfo = {
        ...state.statusInfo,
        ...status_info,
      };
      state.conversationNpcId = conversation_npc_id || null;
    });
    builder
      .addCase(submitWorkspaceDraft.pending, (state, action) => {
        state.submitLoading = true;
        state.isToastVisible = false;
      })
      .addCase(
        submitWorkspaceDraft.fulfilled,
        (state, action: PayloadAction<SubmitWorkspaceDraftResp>) => {
          state.submitLoading = false;
          state.isToastVisible = true;
          state.toastStatus = action.payload?.fail_reason ? 'warning' : 'success';
          state.toastInfo = action.payload;
        }
      )
      .addCase(submitWorkspaceDraft.rejected, (state, action: PayloadAction<any>) => {
        state.submitLoading = false;
        state.isToastVisible = true;
        state.toastStatus = 'error';
        state.toastInfo = action.payload;
      });
    builder.addCase(getWorkspacePluginList.fulfilled, (state, action) => {
      state.allPluginList = action.payload.plugin_descriptions;
    });
    builder.addCase(getChatModels.fulfilled, (state, action) => {
      state.chatModels = ((action.payload.chat_model_detail || {}).chat_model_meta_list || []).map(
        (m) => ({
          id: m.chat_model_type,
          name: m.chat_model_name,
          desc: m.chat_model_detail,
        })
      );
    });
  },
});

const { actions } = editSlice;
export const {
  updateSettingInfo,
  updateImgInfo,
  updateVoiceInfo,
  mergeVoiceInfo,
  updateStatusInfo,
  updateLocalCheckSnap,
  setEditingImageUrl,
  setEditingMaterialId,
  setVoiceUrl,
  setInfoModalVisibility,
  setPersonaLogicModalVisibility,
  setNpcSettings,
  setUseNpcSettings,
  setToastVisible,
  setToastInfo,
  setToastStatus,
  setNpcSettingsLoading,
  updateModifiedFields,
  selectPlugin,
  unselectPlugin,
  setPluginPrompt,
  setPluginImageConfig,
  setShowEditImage,
} = actions;
